import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import api from '../api/client';
import { actionBtnSx } from '../styles/uiTokens';
import { getAdminRole, getAdminStoreId } from '../utils/auth';

const placementLabel = {
  home_top: '홈 상단',
  search_result: '검색 결과',
  category: '카테고리',
};

const placements = ['home_top', 'search_result', 'category'];

const BannersPage = () => {
  // role/storeId 는 JWT 에서 (localStorage 변조 방지)
  const role = getAdminRole();
  const isMaster = role === 'master';
  const myStoreId = getAdminStoreId() || '';

  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);

  // 생성/수정 모달
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formTitle, setFormTitle] = useState('');
  const [formImageUrl, setFormImageUrl] = useState('');
  const [formLinkUrl, setFormLinkUrl] = useState('');
  const [formPlacement, setFormPlacement] = useState('home_top');
  const [formIsActive, setFormIsActive] = useState(true);
  // 빈 문자열 = 글로벌(전체 공통)
  const [formStoreId, setFormStoreId] = useState('');

  useEffect(() => {
    fetchBanners();
    const url = isMaster ? '/stores/admin' : '/stores/admin/my';
    api
      .get(url)
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [res.data].filter(Boolean);
        setStores(list);
      })
      .catch(() => setStores([]));
  }, [isMaster]);

  const storeName = (sid) => {
    if (!sid) return '전체 공통';
    const s = stores.find((x) => x._id === sid);
    return s?.name ?? '매장 정보 없음';
  };

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const res = await api.get('/banners/admin');
      setBanners(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('배너 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      await api.patch(`/banners/admin/${id}`, { isActive: !currentStatus });
      fetchBanners();
    } catch (err) {
      alert('상태 변경 실패');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('정말로 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/banners/admin/${id}`);
      fetchBanners();
    } catch (err) {
      alert('삭제 실패');
    }
  };

  const openCreateModal = () => {
    setEditingBanner(null);
    setFormTitle('');
    setFormImageUrl('');
    setFormLinkUrl('');
    setFormPlacement('home_top');
    setFormIsActive(true);
    setFormStoreId(isMaster ? '' : myStoreId);
    setModalOpen(true);
  };

  const openEditModal = (banner) => {
    setEditingBanner(banner);
    setFormTitle(banner.title || '');
    setFormImageUrl(banner.imageUrl || '');
    setFormLinkUrl(banner.linkUrl || '');
    setFormPlacement(banner.placement || 'home_top');
    setFormIsActive(banner.isActive);
    setFormStoreId(banner.storeId || '');
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formTitle.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }
    const payload = {
      title: formTitle,
      imageUrl: formImageUrl,
      linkUrl: formLinkUrl,
      placement: formPlacement,
      isActive: formIsActive,
      storeId: formStoreId || null,
    };
    try {
      if (editingBanner) {
        await api.patch(`/banners/admin/${editingBanner._id}`, payload);
        alert('수정 완료');
      } else {
        await api.post('/banners/admin', { ...payload, sortOrder: banners.length + 1 });
        alert('추가 완료');
      }
      setModalOpen(false);
      fetchBanners();
    } catch (err) {
      const msg = err?.response?.data?.message;
      alert(msg || (editingBanner ? '수정 실패' : '추가 실패'));
    }
  };

  const canModifyBanner = (banner) =>
    isMaster || (banner.storeId && String(banner.storeId) === myStoreId);

  if (loading) {
    return <Typography sx={{ py: 4, textAlign: 'center' }}>로딩 중...</Typography>;
  }

  return (
    <Box>
      {/* 헤더: 제목 + 추가 버튼 한 줄에 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, borderBottom: '3px solid #1976d2', pb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
          배너 관리
        </Typography>
        <Button variant="contained" onClick={openCreateModal} sx={actionBtnSx}>
          + 새 배너
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f9f9f9' }} variant="outlined">
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          💡 배너 관리 안내
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
          • <strong>수정:</strong> "수정" 버튼을 클릭하여 배너 정보와 이미지를 변경할 수 있습니다.<br />
          • <strong>활성화/비활성화:</strong> 버튼을 클릭하여 배너 상태를 변경할 수 있습니다.<br />
          • <strong>삭제:</strong> "삭제" 버튼을 클릭하여 배너를 완전히 제거할 수 있습니다.
        </Typography>
      </Paper>

      {banners.length > 0 ? (
        <Grid container spacing={3}>
          {banners.map((banner, index) => (
            // MUI v9 Grid: size 프로퍼티 사용 (구버전 item xs={} 는 무시됨)
            // 배너는 보통 소수라 카드를 크게: 모바일 1열 / 그 외 2열 (빈 공간 최소화)
            <Grid size={{ xs: 12, sm: 6 }} key={(banner._id || banner.id)}>
              <Paper variant="outlined" sx={{
                overflow: 'hidden',
                border: banner.isActive ? '3px solid #1976d2' : '1px solid #ddd',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}>
                {/*
                  이미지는 원본 비율을 보존하되, 항상 같은 크기의 미리보기 프레임 안에 둔다.
                  aspect-ratio만으로 부모 높이를 계산하면 세로 원본에서 maxHeight가
                  확정되지 않아 카드 전체가 늘어날 수 있으므로 명시적인 반응형 높이를 쓴다.
                */}
                <Box sx={{
                  width: '100%',
                  height: { xs: 220, sm: 260, md: 300 },
                  boxSizing: 'border-box',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#F8FAFC',
                }}>
                  {banner.imageUrl ? (
                    <img
                      src={banner.imageUrl}
                      alt={banner.title}
                      style={{
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        borderRadius: '12px',
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <Box sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <Typography color="text.secondary">이미지 없음</Typography>
                    </Box>
                  )}
                </Box>

                {/* 정보 */}
                <Box sx={{ p: 2 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                    {banner.title || '제목 없음'}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 0.5, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip label={`#${banner.sortOrder}`} size="small" color="primary" sx={{ fontWeight: 700 }} />
                    <Typography variant="body2">📍</Typography>
                    <Chip
                      label={placementLabel[banner.placement] || banner.placement}
                      size="small"
                      color={banner.placement === 'home_top' ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
                    <Chip
                      label={banner.isActive ? '✓ 활성화됨' : '✗ 비활성화됨'}
                      size="small"
                      sx={{
                        backgroundColor: banner.isActive ? '#e3f2fd' : '#ffebee',
                        color: banner.isActive ? '#1976d2' : '#d32f2f',
                        fontWeight: 600,
                      }}
                    />
                    <Chip
                      label={banner.storeId ? storeName(banner.storeId) : '전체 공통'}
                      size="small"
                      sx={{
                        backgroundColor: banner.storeId ? '#fff3e0' : '#e3f2fd',
                        color: banner.storeId ? '#ef6c00' : '#1976d2',
                        fontWeight: 700,
                      }}
                    />
                  </Box>

                  {/* 버튼 — actionBtnSx (알약형/nowrap/flexShrink:0)
                      비활성화는 회색 계열로 분리해 삭제(빨강)와 명확히 구분 */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => openEditModal(banner)}
                      sx={actionBtnSx}
                      disabled={!canModifyBanner(banner)}
                    >
                      수정
                    </Button>
                    <Button
                      variant="contained"
                      color={banner.isActive ? 'inherit' : 'success'}
                      onClick={() => handleToggle((banner._id || banner.id), banner.isActive)}
                      disabled={!canModifyBanner(banner)}
                      sx={{
                        ...actionBtnSx,
                        // 비활성화 버튼: 회색 (삭제 빨강과 구분)
                        ...(banner.isActive && {
                          backgroundColor: '#64748B',
                          color: '#fff',
                          '&:hover': { backgroundColor: '#475569', filter: 'none' },
                        }),
                      }}
                    >
                      {banner.isActive ? '비활성화' : '활성화'}
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => handleDelete((banner._id || banner.id))}
                      sx={actionBtnSx}
                      disabled={!canModifyBanner(banner)}
                    >
                      삭제
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }} variant="outlined">
          <Typography color="text.secondary">등록된 배너가 없습니다.</Typography>
        </Paper>
      )}


      {/* 생성/수정 모달 */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>
          {editingBanner ? '배너 수정' : '배너 추가'}
        </DialogTitle>
        <DialogContent>
          {/* 대상 매장 선택 */}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1, mb: 0.5 }}>
            대상
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <Select
              value={formStoreId}
              onChange={(e) => setFormStoreId(e.target.value)}
              disabled={!isMaster}
              displayEmpty
            >
              {isMaster && (
                <MenuItem value="">
                  <Box component="span" sx={{ color: '#1976d2', fontWeight: 700 }}>
                    전체 공통 (모든 매장에 표시)
                  </Box>
                </MenuItem>
              )}
              {stores.map((s) => (
                <MenuItem key={s._id} value={s._id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
            {!isMaster && (
              <Box sx={{ fontSize: 11, color: '#888', mt: 0.5, ml: 0.5 }}>
                매니저는 본인 매장 배너만 작성할 수 있습니다.
              </Box>
            )}
          </FormControl>

          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
            배너 제목 *
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
            링크 URL
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={formLinkUrl}
            onChange={(e) => setFormLinkUrl(e.target.value)}
            placeholder="https://example.com"
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
            배치 위치
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <Select
              value={formPlacement}
              onChange={(e) => setFormPlacement(e.target.value)}
            >
              {placements.map((p) => (
                <MenuItem key={p} value={p}>{placementLabel[p]}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={formIsActive}
                onChange={(e) => setFormIsActive(e.target.checked)}
              />
            }
            label="활성화"
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
            이미지 URL
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={formImageUrl}
            onChange={(e) => setFormImageUrl(e.target.value)}
            placeholder="https://..."
            sx={{ mb: 2 }}
          />

          {/* 이미지 미리보기 */}
          {formImageUrl && (
            <>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                이미지 미리보기
              </Typography>
              <Box sx={{
                width: '100%',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid #ddd',
              }}>
                <img
                  src={formImageUrl}
                  alt="미리보기"
                  style={{ width: '100%', display: 'block' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>취소</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editingBanner ? '수정' : '추가'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BannersPage;
